import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { MyInput, MyAutocomplete, MyButton } from "../../atoms";
import {
  getProvince,
  getKabKot,
  getKecamatan,
  getKelurahan,
} from "../../../config/Redux/Global/actions";
import "./ModalAddExcoPUK.css";

/**
 * Hybrid Modal: Material UI Dialog wrapper + Plain HTML/CSS Fields
 */
const ModalAddExcoPUK = ({ isOpen, onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const { dataProvince, dataKabKot, dataKecamatan, dataKelurahan } =
    useSelector((state) => state.global);

  // console.log({ dataProvince });
  const [formData, setFormData] = useState({
    namaPT: "",
    provinsi: null,
    kabKota: null,
    kecamatan: null,
    kelurahan: null,
    alamat: "",
    kodePos: "",
  });

  const [errors, setErrors] = useState({});

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.namaPT) newErrors.namaPT = "Nama Basis wajib diisi";
    if (!formData.provinsi) newErrors.provinsi = "Provinsi wajib dipilih";
    if (!formData.kabKota) newErrors.kabKota = "Kab/Kota wajib dipilih";
    if (!formData.kecamatan) newErrors.kecamatan = "Kecamatan wajib dipilih";
    if (!formData.kelurahan) newErrors.kelurahan = "Kelurahan wajib dipilih";
    if (!formData.alamat) newErrors.alamat = "Alamat wajib diisi";

    if (!formData.kodePos) newErrors.kodePos = "Kode Pos wajib diisi";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      id_tingkat_exco: 7,
      id_provinsi: formData.provinsi?.id,
      id_kabkot: formData.kabKota?.id,
      id_kecamatan: formData.kecamatan?.id,
      id_kelurahan: formData.kelurahan?.id,
      tingkat_puk_industri: formData.namaPT,
      alamat_detail: formData.alamat,
      kode_pos: formData.kodePos,
    };

    onSubmit(payload);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(getProvince());
    } else {
      // Reset form and errors when modal closes
      setFormData({
        namaPT: "",
        provinsi: null,
        kabKota: null,
        kecamatan: null,
        kelurahan: null,
        alamat: "",
        kodePos: "",
      });
      setErrors({});
    }
  }, [isOpen, dispatch]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when field has value, show error when empty
    setErrors((prev) => {
      const newErrors = { ...prev };
      const isEmpty = value === null || value === undefined || value === "";

      if (isEmpty) {
        // Add error if field is empty
        const errorMessages = {
          namaPT: "Nama Basis wajib diisi",
          provinsi: "Provinsi wajib dipilih",
          kabKota: "Kab/Kota wajib dipilih",
          kecamatan: "Kecamatan wajib dipilih",
          kelurahan: "Kelurahan wajib dipilih",
          alamat: "Alamat wajib diisi",
          kodePos: "Kode Pos wajib diisi",
        };
        if (errorMessages[name]) {
          newErrors[name] = errorMessages[name];
        }
      } else {
        // Clear error if field has value
        delete newErrors[name];
      }
      return newErrors;
    });

    // Cascading API calls
    if (name === "provinsi") {
      setFormData((prev) => ({
        ...prev,
        kabKota: null,
        kecamatan: null,
        kelurahan: null,
      }));
      if (value?.id) dispatch(getKabKot(value.id));
    }
    if (name === "kabKota") {
      setFormData((prev) => ({ ...prev, kecamatan: null, kelurahan: null }));
      // Match AlamatKantor pattern: provinsi=PROV_ID&kotaKab=KAB_ID
      // But we need formData.provinsi.id.
      // Wait, inside simple handleChange(name, value), 'value' is the NEW value for 'kabKota'.
      // formData.provinsi is accessible.
      if (value?.id) {
        const provId = formData.provinsi?.id;
        dispatch(getKecamatan(`provinsi=${provId}&kotaKab=${value.id}`));
      }
    }
    if (name === "kecamatan") {
      setFormData((prev) => ({ ...prev, kelurahan: null }));
      // Match AlamatKantor pattern: kecamatan=KEC_ID
      if (value?.id) dispatch(getKelurahan(`kecamatan=${value.id}`));
    }
  };

  const mapOptions = (data) => {
    const mapped =
      data?.data?.map((item) => ({
        label:
          item.name ||
          item.nama_provinsi ||
          item.nama_prov_kemdagri ||
          item.nama_kab_kota_kemdagri ||
          item.nama_kec_bps ||
          item.nama_kel_des_bps ||
          item.nama_kab_kot ||
          item.nama_kecamatan ||
          item.nama_kelurahan ||
          "",
        id:
          item.id ||
          item.id_provinsi ||
          item.kode_prov_bps ||
          item.kode_kab_kota_bps ||
          item.kode_kec_bps ||
          item.kode_kel_desa_bps ||
          item.id_kabkot ||
          item.id_kecamatan ||
          item.id_kelurahan,
      })) || [];

    // Deduplicate by ID
    const unique = [];
    const seen = new Set();
    for (const item of mapped) {
      if (item.id && !seen.has(item.id)) {
        seen.add(item.id);
        unique.push(item);
      }
    }
    return unique;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: 12 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "20px" }}>
          Tambah Komisaris Basis
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <div
          className="modal-body"
          style={{
            margin: 0,
            padding: "8px 0",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {/* Nama Basis/PUK */}
          <MyInput
            label="Nama Basis / PUK"
            name="namaPT"
            value={formData.namaPT}
            onChange={(e) => handleChange("namaPT", e.target.value)}
            error={!!errors.namaPT}
            helperText={errors.namaPT}
          />

          {/* Provinsi */}
          <MyAutocomplete
            label="Provinsi"
            options={mapOptions(dataProvince)}
            value={formData.provinsi}
            onChange={(e, val) => handleChange("provinsi", val)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            error={!!errors.provinsi}
            helperText={errors.provinsi}
          />

          {/* Kab/Kota */}
          <MyAutocomplete
            label="Kab/Kota"
            options={mapOptions(dataKabKot)}
            value={formData.kabKota}
            onChange={(e, val) => handleChange("kabKota", val)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            disabled={!formData.provinsi}
            error={!!errors.kabKota}
            helperText={errors.kabKota}
          />

          {/* Kecamatan */}
          <MyAutocomplete
            label="Kecamatan"
            options={mapOptions(dataKecamatan)}
            value={formData.kecamatan}
            onChange={(e, val) => handleChange("kecamatan", val)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            disabled={!formData.kabKota}
            error={!!errors.kecamatan}
            helperText={errors.kecamatan}
          />

          {/* Kelurahan/Desa */}
          <MyAutocomplete
            label="Kelurahan/Desa"
            options={
              // Client-side filtering as backup if API returns all 80k rows
              formData.kecamatan?.id
                ? mapOptions(dataKelurahan).filter(
                    (item) =>
                      item.id &&
                      String(item.id).startsWith(String(formData.kecamatan.id)),
                  )
                : mapOptions(dataKelurahan)
            }
            value={formData.kelurahan}
            onChange={(e, val) => handleChange("kelurahan", val)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            disabled={!formData.kecamatan}
            error={!!errors.kelurahan}
            helperText={errors.kelurahan}
          />

          {/* Kode Pos */}
          <MyInput
            label="Kode Pos"
            name="kodePos"
            value={formData.kodePos}
            onChange={(e) => handleChange("kodePos", e.target.value)}
            error={!!errors.kodePos}
            helperText={errors.kodePos}
            type="number"
          />

          {/* Alamat Lengkap */}
          <MyInput
            label="Alamat Lengkap"
            name="alamat"
            value={formData.alamat}
            onChange={(e) => handleChange("alamat", e.target.value)}
            multiline
            rows={3}
            error={!!errors.alamat}
            helperText={errors.alamat}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "auto", // Override fixed height for textarea
                alignItems: "flex-start",
                padding: "8px",
              },
            }}
          />
        </div>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        {/* Maintain custom buttons or use MUI buttons? 
            Since user disliked MUI fields, they usually prefer the previous look of buttons too.
            However, MUI DialogActions usually maps well with MUI Buttons.
            Let's stick to the CSS class buttons for consistency with the fields request, 
            OR use MUI buttons but styled. The prompt specifically said "fields material ui".
            Let's use the HTML buttons from the CSS file to ensure "exact design" request previously is met.
        */}
        <div className="modal-footer" style={{ width: "100%", margin: 0 }}>
          <button className="btn-cancel" onClick={onClose}>
            Batal
          </button>
          <button className="btn-submit" onClick={handleSubmit}>
            <AddIcon fontSize="small" />
            Tambahkan
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddExcoPUK;
